import { useCallback, useMemo } from "react";

import useZodValidation from "@/hooks/useZodValidation";

import { PREVIOUS_SUFFIX } from "../constants/previous";
import { useFormStoreContext } from "../store/zustand-context";
import type { Attribute, FieldId, FieldValueType } from "../types";
import { parseFieldId } from "../utils/field-id";
import { validateField } from "../utils/validation-rules";

interface UseFormFieldOptions {
  /** フィールドID */
  fieldId: FieldId;

  /** 属性定義 */
  attribute?: Attribute;

  /** 値が変更されたときのコールバック（オプション） */
  onChange?: (value: FieldValueType) => void;

  /** フォーカスが外れたときのコールバック（オプション） */
  onBlur?: () => void;

  /** フォーカスしたときのコールバック（オプション） */
  onFocus?: () => void;
}

interface UseFormFieldResult {
  /** フィールドの値 */
  value: {
    currentValue: FieldValueType | undefined;
    previousValue: FieldValueType | undefined;
  };

  /** フィールドのエラーメッセージ */
  error: string | null;

  /** フィールドがタッチ済みかどうか */
  touched: boolean;

  /** 値が変更されたときのハンドラー */
  handleChange: (value: FieldValueType) => void;

  /** フォーカスが外れたときのハンドラー */
  handleBlur: () => void;

  /** フォーカスしたときのハンドラー */
  handleFocus: () => void;
}

/**
 * フォームフィールドの状態とアクションを管理するフック
 *
 * @param options フックのオプション
 * @returns フィールドの状態とアクション
 */
export const useFormField = ({
  fieldId,
  attribute,
  onChange,
  onBlur,
  onFocus,
}: UseFormFieldOptions): UseFormFieldResult => {
  useZodValidation();
  const store = useFormStoreContext();
  const parsedFieldId = useMemo(() => parseFieldId(fieldId), [fieldId]);

  // 値の取得
  const value = useMemo(() => {
    const fieldValue = store.values.find((v) => v.fieldId === fieldId);
    if (
      attribute?.type !== "ChangeText" &&
      attribute?.type !== "ChangeSingleSelect" &&
      attribute?.type !== "ChangeBoolean" &&
      attribute?.type !== "ChangeInteger" &&
      attribute?.type !== "ChangeDecimal" &&
      attribute?.type !== "ChangeRadio"
    ) {
      return { currentValue: fieldValue?.value, previousValue: undefined };
    }

    // 変更前の値を表示する場合は、該当フィールド名+Previousを取得して値を返す
    return {
      currentValue: fieldValue?.value,
      previousValue: store.values.find(
        (v) => v.fieldId === fieldId + PREVIOUS_SUFFIX,
      )?.value,
    };
  }, [store.values, fieldId, attribute?.type]);

  // エラーの取得
  const error = useMemo(() => {
    return store.getFieldError(fieldId);
  }, [store, fieldId]);

  // タッチ状態の取得
  const touched = useMemo(() => {
    return store.getTouched(fieldId);
  }, [store, fieldId]);

  const handleChange = useCallback(
    (newValue: FieldValueType) => {
      store.removeFieldError(fieldId);
      store.setValue(fieldId, newValue);
      store.setTouched(fieldId, true);

      if (attribute) {
        const fieldError = validateField(attribute, newValue);
        if (!fieldError.isValid && fieldError.errorMessage) {
          store.setFieldError(fieldId, fieldError.errorMessage);
        }
      }

      onChange?.(newValue);
    },
    [store, fieldId, attribute, onChange],
  );

  /**
   * フォーカスしたときのハンドラー
   */
  const handleFocus = useCallback(() => {
    onFocus?.();
  }, [onFocus]);

  /**
   * タッチ状態を設定し、バリデーションを実行する
   */
  const handleBlur = useCallback(() => {
    if (!parsedFieldId) {
      return;
    }

    store.setTouched(fieldId, true);
    const currentValue = store.values.find((v) => v.fieldId === fieldId)?.value;
    if (attribute) {
      const fieldError = validateField(attribute, currentValue);
      if (!fieldError.isValid && fieldError.errorMessage) {
        store.setFieldError(fieldId, fieldError.errorMessage);
      } else {
        store.removeFieldError(fieldId);
      }
    }
    onBlur?.();
  }, [store, fieldId, parsedFieldId, attribute, onBlur]);

  return useMemo(
    () => ({
      value,
      error,
      touched,
      handleChange,
      handleBlur,
      handleFocus,
    }),
    [value, error, touched, handleChange, handleBlur, handleFocus],
  );
};
